// ImyBoss Agency Panel — React entry. Loads the agency dashboard module
// (attaches to window.MlAgency) plus its two shared dependencies (icons,
// modals), then mounts the auth-gated Root. React/ReactDOM are UMD globals
// (index.html); the DS bundle (window.HuddleDesignSystem_775113) is loaded
// there too. Vite transpiles the JSX at build time against the global React.

// --- Stylesheets (cascade after the DS CSS in index.html) ---
import './styles/marketlens.css';
import './styles/bi.css';

// App-side wiring
import { setReadOnly, getMe, logout as apiLogout } from './lib/api.js';

// --- Feature modules (side-effect imports → window.Ml*; order matters) ---
import './app/icons.jsx';
import './app/bi-modals.jsx';
import './app/agency.jsx';

(function mount() {
  const DS = window.HuddleDesignSystem_775113;
  if (!DS || !window.React || !window.ReactDOM || !window.MlIcon || !window.MlBiModals || !window.MlAgency) {
    return void setTimeout(mount, 30);
  }
  const { useState, useEffect, useCallback } = React;
  const { AgencyApp } = window.MlAgency;

  // --- Auth gate — ImyBoss ID (auth.imyboss.app) is the only sign-in method.
  // Auto-redirects into the OIDC flow; button as fallback (e.g. on errors).
  function AuthGate() {
    const { Button, Banner } = DS;
    const ssoError = new URLSearchParams(window.location.search).get("sso_error");
    const [redirecting, setRedirecting] = useState(false);
    const go = () => { setRedirecting(true); window.location.href = "/auth/login"; };
    useEffect(() => { if (!ssoError) go(); }, []);
    return (
      <div className="ml-auth">
        <div className="ml-auth__card">
          <div className="ml-auth__head">
            <h1>Sign in to the Agency Panel</h1>
            <p>One ImyBoss ID for every ImyBoss app.</p>
          </div>
          <div className="ml-auth__form">
            {ssoError && <Banner tone="danger" title="Sign-in failed">Please try again.</Banner>}
            <Button variant="primary" fullWidth disabled={redirecting} onClick={go}>
              {redirecting ? "Redirecting…" : "Sign in with ImyBoss ID"}
            </Button>
            <p style={{ marginTop: 12, fontSize: 12, opacity: 0.7, textAlign: "center" }}>
              Passwords and two-factor settings live in your ImyBoss account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- Bootstrap: auth gate → render the agency dashboard ---
  function Root() {
    const [phase, setPhase] = useState("loading");   // loading | login | app
    const [me, setMe] = useState(null);

    const boot = useCallback(async () => {
      setPhase("loading");
      const meRes = await getMe();
      setMe(meRes);
      // Agency Analyst (member on the agency account) is read-only across tenants.
      setReadOnly(!!(meRes.role === "viewer" || (meRes.role === "member" && meRes.account && meRes.account.is_agency)));
      if (meRes.auth_required && !meRes.authenticated) { setPhase("login"); return; }
      setPhase("app");
    }, []);

    useEffect(() => { boot(); }, [boot]);
    useEffect(() => {
      const onAuthReq = () => setPhase("login");
      document.addEventListener("auth-required", onAuthReq);
      return () => document.removeEventListener("auth-required", onAuthReq);
    }, []);

    const onLogout = useCallback(async () => {
      try { await apiLogout(); } catch { /* */ }
      const u = new URL("https://auth.imyboss.app/oidc/v1/end_session");
      u.searchParams.set("post_logout_redirect_uri", window.location.origin);
      window.location.assign(u.toString());
    }, []);

    if (phase === "loading") return <div style={{ display: "grid", placeItems: "center", height: "100vh", color: "var(--text-muted)", font: "var(--text-sm) var(--font-sans)" }}>Loading…</div>;
    if (phase === "login") return <AuthGate />;
    const readOnly = !!(me && (me.role === "viewer" || (me.role === "member" && me.account && me.account.is_agency)));
    // Standalone panel: "exit" signs out; the workspace-console footer link
    // points back at the tenant-facing app (MarketLens) for now.
    // `role` goes through as well as `readOnly`: owner/admin-only actions (e.g.
    // manual access management on the tenant drill-down) need the role itself,
    // not just whether this staff member can write at all.
    return <AgencyApp onExit={onLogout} onConsole={() => { window.location.href = "https://marketlens.imyboss.app"; }} readOnly={readOnly} role={me && me.role} />;
  }

  ReactDOM.createRoot(document.getElementById('root')).render(<Root />);
})();
